import type { Role as ApiRole, GetRolesParams } from '@kizen/api/role';
import type { QueryClient } from '@tanstack/react-query';
import type { SettingsState } from 'pages/Settings/SettingsContext';
import type { SortColumn } from 'utility/SortingHelpers';
import type { TeamPageProps } from './types';

import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'react-use';
import { KizenTypography } from '__app/typography';
import Button from '__components/Button';
import Loader from 'components/Kizen/Loader';
import { TRow } from '__components/Kizen/Table';
import { BasicTableWrapper } from '__components/Tables/Basic';
import TablePagination, {
  TablePaginationWrapper,
} from 'components/Tables/Big/TablePagination';
import { PageSizing } from '__components/Layout/PageContentWidth';
import { ContentWidth } from '__components/Layout/PageContentWidth';
import BasicModal from '__components/Modals/presets/BasicModal';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';
import ConfirmNavigationModal from '__components/Modals/presets/ConfirmNavigation';
import BasicTable from '__components/Tables/Basic';
import TableScrollContainer from '__components/Tables/ScrollContainer';
import { toastVariant, useToast } from '__components/ToastProvider';
import { RoleHistoryModal } from '__pages/Settings/pages/Team/HistoryModal';
import { invalidate } from '__queries/invalidate';
import { permissionGroupsQuery as getPermissionGroupsQuery } from 'query/permission-group';
import { rolesQuery as getRolesQuery } from 'query/role';
import { teamMembersQuery, searchTeamMembersQuery } from 'query/team-member';
import { getOriginalError } from 'services/AxiosService';
import TeamMemberService from 'services/TeamMemberService';
import { camelToSnakeCase, snakeToCamelCaseKeys } from '__services/helpers';
import { serviceRefreshAccessStart } from 'store/authentication/authenticationAction';
import { getChosenBusiness } from 'store/authentication/selectors';
import { DEFAULT_INPUT_DELAY } from 'utility/config';
import { getOrderingParam, getSortValue } from 'utility/SortingHelpers';
import { useSettingsContext } from '../../SettingsContext';
import { ApplyTemplateModal } from './ApplyTemplateModal';
import { APPLY_TEMPLATES_TYPES } from './constants';
import { getRoleColumns } from './columns';
import RoleForm, { validateRoleForm } from './RoleForm';
import { Toolbar } from './Toolbar';
import { openMenuAboveMeta } from './util';

const TEAM_MEMBERS_QUERY_KEY = searchTeamMembersQuery().queryKey;

type Role = SnakeToCamelCase<ApiRole>;

interface EditableRole
  extends Pick<Role, 'defaultForNewUsers' | 'name' | 'permissionGroups'> {
  id?: string;
}

type Action =
  | 'apply-columns'
  | 'apply-quick-filters'
  | 'apply-toolbar'
  | 'delete'
  | 'duplicate'
  | 'edit'
  | 'history';

type TemplateModal = {
  type: 'toolbars' | 'columns' | 'quickFilters';
  data: Role;
};

const invalidateRolesQuery = (qc: QueryClient) => {
  invalidate.ROLES.ALL(); // used in components/Inputs/Select/hooks.js
  invalidate.TEAM_MEMBERS.ROLES(); // used in components/Fields/InlineTableFieldInput/TeamMemberRoles.jsx
  qc.invalidateQueries({
    queryKey: getRolesQuery().queryKey,
  });
};

export const RolesPage = ({ access }: TeamPageProps) => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const [showToast] = useToast();

  const chosenBusiness = useSelector(getChosenBusiness);

  const ctx = useSettingsContext();
  const { search, sort, setSearch, setSort } = ctx.roles;

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [role, setRole] = useState<EditableRole | null>(null);

  const [roleHistoryId, setRoleHistoryId] = useState<string | null>(null);
  const [templateModal, setTemplateModal] = useState<TemplateModal | null>(
    null
  );
  const [errors, setErrors] = useState<{ name: string } | null>(null);
  const [formDirty, setFormDirty] = useState(false);
  const [confirmFormDiscard, setConfirmFormDiscard] = useState(false);

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  useDebounce(() => setDebouncedSearch(search), DEFAULT_INPUT_DELAY, [search]);

  const rolesQueryOptions = useMemo(() => {
    const params: GetRolesParams<'paginated'> = {
      page,
      page_size: pageSize,
      ordering: sort,
    };

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    return getRolesQuery({
      params,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      select: (data) => {
        const { results, ...rest } = data;
        const mapped = {
          ...snakeToCamelCaseKeys(rest),
          results: results.map((role, index, arr) => {
            return {
              ...snakeToCamelCaseKeys(role),
              meta: openMenuAboveMeta({ index, total: arr.length }),
            };
          }),
        };
        return mapped;
      },
    });
  }, [page, pageSize, debouncedSearch, sort]);

  const rolesQuery = useQuery(rolesQueryOptions);

  const permissionGroupsQuery = useQuery(
    getPermissionGroupsQuery({
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      select: (data) => {
        return snakeToCamelCaseKeys(data);
      },
    })
  );

  const handleChangeSearch = useCallback(
    (value: string) => {
      setSearch(value);
      setPage(1);
    },
    [setSearch]
  );

  const { admin_permission_role: adminPermissionRole } = chosenBusiness;

  const roleHeadData = useMemo(() => {
    return {
      meta: {
        sort: sort ? getSortValue(sort) : null,
        onSort: (
          column: SnakeToCamelCase<SortColumn<SettingsState['roles']['sort']>>,
          direction: 'asc' | 'desc'
        ) => {
          const snake = camelToSnakeCase(column);
          const sort = getOrderingParam({ column: snake, direction });
          setSort(sort);
        },
      },
    };
  }, [sort, setSort]);

  const roleColumns = useMemo(() => {
    return getRoleColumns(
      {
        onClickName: (role: Role) => {
          setRole(role);
        },
        onSelectAction: async (action: { value: Action }, role: Role) => {
          switch (action.value) {
            case 'delete':
              setDeleteId(role.id);
              break;
            case 'duplicate':
              try {
                await TeamMemberService.duplicateRole(role.id);
              } finally {
                invalidateRolesQuery(qc);
              }
              break;
            case 'edit':
              setRole(role);
              break;
            case 'apply-toolbar':
              setTemplateModal({
                type: 'toolbars',
                data: role,
              });
              break;
            case 'apply-columns':
              setTemplateModal({
                type: 'columns',
                data: role,
              });
              break;
            case 'apply-quick-filters':
              setTemplateModal({
                type: 'quickFilters',
                data: role,
              });
              break;
            case 'history':
              setRoleHistoryId(role.id);
              break;
            default:
              break;
          }
        },
      },
      t,
      access,
      adminPermissionRole
    );
  }, [t, qc, access, adminPermissionRole, setRoleHistoryId]);

  if (rolesQuery.isLoading) {
    return <Loader loading />;
  }

  return (
    <PageSizing>
      <ConfirmNavigationModal
        when={formDirty}
        show={confirmFormDiscard}
        onHide={() => {
          setConfirmFormDiscard(false);
        }}
        onConfirm={() => {
          setRole(null);
          setErrors(null);
          setConfirmFormDiscard(false);
          setFormDirty(false);
        }}
      />
      {deleteId && (
        <ConfirmDeletionModal
          show
          typeOfContent="text"
          defaultLeftBtnText={t('Cancel')}
          onHide={() => setDeleteId(null)}
          onConfirm={async () => {
            try {
              await TeamMemberService.deleteRole(deleteId);
              qc.invalidateQueries({ queryKey: TEAM_MEMBERS_QUERY_KEY });
            } catch (err: any) {
              const { data } = err.response;

              if (data.statusCode === 400) {
                showToast({
                  variant: toastVariant.FAILURE,
                  delay: 3000,
                  message: data.errors[0],
                });
              }
            } finally {
              setDeleteId(null);
              invalidateRolesQuery(qc);
              dispatch(serviceRefreshAccessStart() as any);
            }
          }}
        >
          <KizenTypography>
            {t('This will permanently delete the Role')}
          </KizenTypography>
        </ConfirmDeletionModal>
      )}
      {templateModal ? (
        <ApplyTemplateModal
          show
          applyTo={APPLY_TEMPLATES_TYPES.roles}
          templateType={templateModal.type}
          data={templateModal.data}
          onHide={() => setTemplateModal(null)}
        />
      ) : null}
      {roleHistoryId && (
        <RoleHistoryModal
          id={roleHistoryId}
          onHide={() => setRoleHistoryId(null)}
        />
      )}
      {role && (
        <BasicModal
          show
          heading={`${role.id ? t('Edit') : t('Create')} ${t('Role')}`}
          buttonText={t('Save')}
          disabled={!validateRoleForm(role)}
          leftBtn={null}
          enforceFocus={false}
          onHide={() => {
            if (formDirty) {
              setConfirmFormDiscard(true);
            } else {
              setRole(null);
              setErrors(null);
            }
          }}
          onConfirm={async () => {
            const isEdit = Boolean(role.id);
            const payload = {
              default_for_new_users: role.defaultForNewUsers,
              name: role.name,
              permission_groups: role.permissionGroups.map((pg) => pg.id),
            };

            try {
              if (isEdit) {
                await TeamMemberService.updateRole({ id: role.id }, payload);
              } else {
                await TeamMemberService.addRole(payload);
              }

              setErrors(null);
              setRole(null);
              setFormDirty(false);
            } catch (err: any) {
              const { data, status } = err.response;

              if (status === 400) {
                const originalError = getOriginalError(err);

                if (originalError) {
                  setErrors(snakeToCamelCaseKeys(originalError));
                } else {
                  showToast({
                    variant: toastVariant.FAILURE,
                    delay: 3000,
                    message: data.errors.detail,
                  });
                }
              }
            } finally {
              invalidateRolesQuery(qc);

              if (isEdit) {
                dispatch(serviceRefreshAccessStart() as any);
                qc.invalidateQueries({
                  queryKey: teamMembersQuery().queryKey,
                });
              }
            }
          }}
        >
          <RoleForm
            permissionGroups={permissionGroupsQuery.data ?? []}
            values={role}
            fieldErrors={errors}
            onChange={(field: keyof EditableRole, value: any) => {
              setFormDirty(true);
              setRole((prev) => {
                return { ...prev!, [field]: value };
              });

              if (field === 'name' && value?.trim()?.length > 0) {
                setErrors(null);
              }
            }}
          />
        </BasicModal>
      )}
      <ContentWidth>
        <Toolbar.Container>
          <Toolbar.LeftColumn>
            <Toolbar.SearchInput
              loading={rolesQuery.isFetching}
              placeholder={t('Find Roles')}
              value={search}
              onChange={handleChangeSearch}
            />
          </Toolbar.LeftColumn>
          <Toolbar.Title as="h2" type="header">
            {rolesQuery.data?.count.toLocaleString()}{' '}
            {rolesQuery.data?.count === 1 ? t('Role') : t('Roles')}
          </Toolbar.Title>
          <Toolbar.RightColumn>
            <Button
              noSpace
              data-qa="add-role-button"
              onClick={() => {
                setRole({
                  name: '',
                  permissionGroups: [],
                  defaultForNewUsers: false,
                });
              }}
            >
              {t('New Role')}
            </Button>
          </Toolbar.RightColumn>
        </Toolbar.Container>
        <BasicTableWrapper className="shadow-tile">
          <TableScrollContainer bottom>
            <BasicTable
              stickyHeader
              data-qa="role-table"
              head={<TRow head columns={roleColumns} data={roleHeadData} />}
            >
              {rolesQuery.data?.results?.map((role) => (
                <TRow key={role.id} columns={roleColumns} data={role} />
              ))}
            </BasicTable>
          </TableScrollContainer>
        </BasicTableWrapper>
        <TablePaginationWrapper>
          <TablePagination
            page={page}
            perPage={pageSize}
            totalCount={rolesQuery.data?.count}
            onChangePage={(value: number) => {
              setPage(value);
            }}
            onChangePerPage={(value: number) => {
              setPageSize(value);
            }}
          />
        </TablePaginationWrapper>
      </ContentWidth>
    </PageSizing>
  );
};
